import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import styled from "styled-components";
import SellerService from "../../../../services/seller.service";
import { useGlobal } from "../../../../context/global";
import GeneralInput from "../../../common/GeneralInput";
import Select from "../../../common/Select";
import FileInput from "../../../common/FileInput";
import { CashCategory, CashFlowType, CashMovement } from "../../../../types/common.types";
import formatNumberToString from "../../../../utils/formatNumber";

function extractMessage(error: any) {
    // Igual criterio que en Caja.tsx: solo se muestra tal cual el mensaje de un error esperado
    // (400/403/404, ya redactado en el backend para el usuario); un 500 queda genérico.
    const status = error?.response?.status
    if (status && status < 500 && typeof error?.response?.data === 'string') return error.response.data
    return 'Ocurrió un error, intenta de nuevo'
}

interface CashMovementFormProps {
    shiftDate: string
    period: 'AM' | 'PM'
    onMovementCreated?: () => void
}

export default function CashMovementForm({ shiftDate, period, onMovementCreated }: CashMovementFormProps) {
    const { setIsLoading } = useGlobal()
    const [isOpen, setIsOpen] = useState(false)
    const [categories, setCategories] = useState<CashCategory[]>([])
    const [movementType, setMovementType] = useState<CashFlowType>('egreso')
    const [amount, setAmount] = useState('')
    const [categoryId, setCategoryId] = useState('')
    const [counterpartName, setCounterpartName] = useState('')
    const [detail, setDetail] = useState('')
    const [photo, setPhoto] = useState<File | undefined>()
    const [movements, setMovements] = useState<CashMovement[]>([])

    async function loadMovements() {
        try {
            const res = await SellerService.listCashMovements(shiftDate, period)
            setMovements(res.data)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        loadMovements()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shiftDate, period])

    useEffect(() => {
        if (!isOpen) return
        SellerService.listCashCategories().then(res => setCategories(res.data)).catch(console.log)
    }, [isOpen])

    const filteredCategories = categories.filter(c => c.flow_type === movementType).sort((a, b) => a.name.localeCompare(b.name))

    function resetForm() {
        setAmount('')
        setCategoryId('')
        setCounterpartName('')
        setDetail('')
        setPhoto(undefined)
    }

    async function onSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setIsLoading(true)
        try {
            const formData = new FormData()
            formData.append('movement_type', movementType)
            formData.append('amount', amount)
            formData.append('category_id', categoryId)
            if (counterpartName) formData.append('counterpart_name', counterpartName)
            if (detail) formData.append('detail', detail)
            if (photo) formData.append('photo', photo)
            await SellerService.createCashMovement(formData)
            resetForm()
            setIsOpen(false)
            await loadMovements()
            // El monto "esperado" para cerrar vive en el componente padre (Caja.tsx) — sin este aviso,
            // quedaba con el valor de cuando se abrió la caja hasta que alguien recargara la página.
            onMovementCreated?.()
        } catch (error: any) {
            alert(extractMessage(error))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Main>
            {!isOpen && (
                <button type="button" className="toggle" onClick={() => setIsOpen(true)}>+ Registrar movimiento de efectivo</button>
            )}
            {isOpen && (
            <form onSubmit={onSubmit}>
                <h3>Nuevo movimiento (Efectivo)</h3>
                <div className="type-toggle">
                    <button
                        type="button"
                        className={movementType === 'ingreso' ? 'active ingreso' : ''}
                        onClick={() => { setMovementType('ingreso'); setCategoryId('') }}
                    >
                        Ingreso
                    </button>
                    <button
                        type="button"
                        className={movementType === 'egreso' ? 'active egreso' : ''}
                        onClick={() => { setMovementType('egreso'); setCategoryId('') }}
                    >
                        Egreso
                    </button>
                </div>
                <GeneralInput
                    label="Monto"
                    type="number"
                    required
                    value={amount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                />
                <Select
                    label="Categoría"
                    required
                    defaultNull
                    value={categoryId}
                    options={filteredCategories.map(c => ({ value: c.id, name: c.name }))}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setCategoryId(e.target.value)}
                />
                <GeneralInput
                    label="Contraparte (opcional)"
                    value={counterpartName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setCounterpartName(e.target.value)}
                />
                <GeneralInput
                    label="Detalle (opcional)"
                    value={detail}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setDetail(e.target.value)}
                />
                <FileInput
                    label="Foto de respaldo (opcional)"
                    value={photo}
                    capture="environment"
                    deleteFile={() => setPhoto(undefined)}
                    accept=".png, .jpg, .jpeg, .heic, .webp"
                    onChange={(newFile: File) => setPhoto(newFile)}
                />
                <div className="actions">
                    <button type="button" className="cancel" onClick={() => { resetForm(); setIsOpen(false) }}>Cancelar</button>
                    <button type="submit" className="submit">Guardar movimiento</button>
                </div>
            </form>
            )}
            {movements.length > 0 && (
                <MovementsList>
                    <h4>Movimientos de este turno</h4>
                    {movements.map(m => {
                        const isMine = !!m.created_by_seller_id
                        return (
                            <div className={`row ${isMine ? 'mine' : 'admin'}`} key={m.id}>
                                <div className="info">
                                    <span className="tag">{isMine ? 'Vendedor' : `Admin${m.created_by_admin_name ? ` (${m.created_by_admin_name})` : ''}`}</span>
                                    <span className="category">{m.category?.name}{m.counterpart_name ? ` — ${m.counterpart_name}` : ''}</span>
                                </div>
                                <span className={`amount ${m.movement_type}`}>
                                    {m.movement_type === 'ingreso' ? '+' : '-'}${formatNumberToString(Number(m.amount))}
                                </span>
                            </div>
                        )
                    })}
                </MovementsList>
            )}
        </Main>
    )
}

const MovementsList = styled.div`
margin-top: 15px;
h4 {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
}
.row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    margin-bottom: 6px;
    border-left: 4px solid transparent;
    .info {
        display: flex;
        flex-direction: column;
        .tag {
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: .3px;
        }
        .category {
            font-size: 13px;
        }
    }
    .amount {
        font-weight: 700;
        white-space: nowrap;
    }
    .amount.ingreso {
        color: ${props => props.theme.blue};
    }
    .amount.egreso {
        color: ${props => props.theme.lightRed};
    }
}
.row.mine {
    background-color: ${props => props.theme.whiteBone};
    border-left-color: ${props => props.theme.blue};
    .tag { color: ${props => props.theme.blue}; }
}
.row.admin {
    background-color: #fdf1e0;
    border-left-color: ${props => props.theme.yellowDark};
    .tag { color: ${props => props.theme.yellowDark}; }
}
`

const Main = styled.div`
.toggle {
    width: 100%;
    padding: 12px;
    border-radius: 8px;
    border: 2px dashed ${props => props.theme.gray};
    background: transparent;
    color: ${props => props.theme.gray};
    font-weight: 600;
    cursor: pointer;
}
form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 15px;
    border-radius: 10px;
    background-color: ${props => props.theme.whiteBone};
    h3 {
        font-size: 16px;
        font-weight: 500;
    }
}
.type-toggle {
    display: flex;
    gap: 10px;
    button {
        flex: 1;
        padding: 12px;
        font-size: 14px;
        border-radius: 8px;
        border: 2px solid ${props => props.theme.gray};
        background-color: ${props => props.theme.white};
        cursor: pointer;
    }
    button.active.ingreso {
        border-color: ${props => props.theme.blue};
        background-color: ${props => props.theme.blue};
        color: ${props => props.theme.white};
    }
    button.active.egreso {
        border-color: ${props => props.theme.lightRed};
        background-color: ${props => props.theme.lightRed};
        color: ${props => props.theme.white};
    }
}
.actions {
    display: flex;
    gap: 10px;
    margin-top: 5px;
    button {
        flex: 1;
        padding: 12px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
    }
    .cancel {
        border: 2px solid ${props => props.theme.gray};
        background: transparent;
        color: ${props => props.theme.gray};
    }
    .submit {
        border: none;
        background-color: ${props => props.theme.blue};
        color: ${props => props.theme.white};
    }
}
`
